import React from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  Users, 
  Target, 
  Play, 
  Pause, 
  CheckCircle,
  AlertTriangle,
  Trophy,
  Timer,
  MapPin
} from 'lucide-react';

const EventStatusCard = ({ activeEvent = null }) => {
  const formatCurrency = (amount) => {
    return `$${Number(amount || 0).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Fecha no definida';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '--:--';
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-CO', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return Play;
      case 'paused': return Pause;
      case 'completed': return CheckCircle;
      case 'cancelled': return AlertTriangle;
      default: return Clock;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'completed': return 'primary';
      case 'cancelled': return 'danger';
      default: return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Activo';
      case 'paused': return 'Pausado';
      case 'completed': return 'Completado';
      case 'cancelled': return 'Cancelado';
      default: return 'Sin definir';
    }
  };

  if (!activeEvent) {
    return (
      <div className="card p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 rounded-xl bg-gray-100 dark:bg-gray-700">
            <Calendar className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              🎯 Estado del Evento
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Información del evento actual
            </p>
          </div>
        </div>

        <div className="text-center py-12">
          <Target className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Sin evento activo
          </h4>
          <p className="text-gray-600 dark:text-gray-400">
            No estás participando en ningún evento en este momento
          </p>
        </div>
      </div>
    );
  }

  const StatusIcon = getStatusIcon(activeEvent.status);
  const statusColor = getStatusColor(activeEvent.status);

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className={`p-3 rounded-xl bg-${statusColor}-100 dark:bg-${statusColor}-900/30`}>
            <StatusIcon className={`w-6 h-6 text-${statusColor}-600 dark:text-${statusColor}-400`} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              🎯 Estado del Evento
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {activeEvent.name}
            </p>
          </div>
        </div>

        <span className={`px-3 py-1 rounded-full text-xs font-bold bg-${statusColor}-100 dark:bg-${statusColor}-900/50 text-${statusColor}-700 dark:text-${statusColor}-300`}>
          {getStatusText(activeEvent.status)}
        </span>
      </div>

      {/* Event Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="flex items-center p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg mr-3">
            <Calendar className="w-4 h-4 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Fecha del Evento
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {formatDate(activeEvent.eventDate)}
            </p>
          </div>
        </div>

        <div className="flex items-center p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
          <div className="p-2 bg-warning-100 dark:bg-warning-900/30 rounded-lg mr-3">
            <Timer className="w-4 h-4 text-warning-600 dark:text-warning-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Hora de Inicio
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {formatTime(activeEvent.eventDate)}
            </p>
          </div>
        </div>

        <div className="flex items-center p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-3">
            <Target className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Semilla del Evento
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {formatCurrency(activeEvent.eventSeedAmount)}
            </p>
          </div>
        </div>
      </div>

      {/* Match Info */}
      {activeEvent.homeTeam && activeEvent.awayTeam && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            ⚽ Partido del Día
          </h4>
          
          <div className="p-4 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="text-center flex-1">
                <div className="w-12 h-12 bg-white dark:bg-dark-800 rounded-full mx-auto mb-2 flex items-center justify-center shadow-sm">
                  <span className="text-lg font-bold text-gray-700 dark:text-gray-300">
                    {activeEvent.homeTeam.charAt(0)}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {activeEvent.homeTeam}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Local</p>
              </div>

              <div className="text-center px-6">
                <div className="text-2xl font-bold text-gray-600 dark:text-gray-400 mb-1">
                  VS
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  {formatTime(activeEvent.eventDate)}
                </p>
              </div>

              <div className="text-center flex-1">
                <div className="w-12 h-12 bg-white dark:bg-dark-800 rounded-full mx-auto mb-2 flex items-center justify-center shadow-sm">
                  <span className="text-lg font-bold text-gray-700 dark:text-gray-300">
                    {activeEvent.awayTeam.charAt(0)}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {activeEvent.awayTeam}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Visitante</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Participation Status */}
      {activeEvent.isParticipating && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-4 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg mb-6"
        >
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-success-100 dark:bg-success-900/50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-success-600 dark:text-success-400" />
            </div>
            <div className="flex-1">
              <h5 className="text-sm font-medium text-success-700 dark:text-success-300 mb-1">
                ✅ Participando en el evento
              </h5>
              <p className="text-xs text-success-600 dark:text-success-400">
                Estás en el Paso {activeEvent.currentStep} de 13. Tu próxima apuesta será de {formatCurrency(activeEvent.nextBetAmount)}.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Event Progress */}
      {activeEvent.isParticipating && (
        <div>
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600 dark:text-gray-400">Progreso del evento</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">
              Paso {activeEvent.currentStep} / 13
            </span>
          </div>
          
          <div className="w-full bg-gray-200 dark:bg-dark-700 rounded-full h-3 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(activeEvent.currentStep / 13) * 100}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className={`h-full rounded-full ${
                activeEvent.currentStep <= 5 ? 'bg-gradient-success' :
                activeEvent.currentStep <= 9 ? 'bg-gradient-warning' :
                'bg-gradient-danger'
              }`}
            />
          </div>
          
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-2">
            <span>Inicio</span>
            <span>Zona de riesgo</span>
            <span>Máximo</span>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-dark-700">
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <Trophy className="w-4 h-4 text-primary-600 dark:text-primary-400" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {formatCurrency(activeEvent.totalPotential || 0)}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Ganancia Potencial</p>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <div className="p-2 bg-warning-100 dark:bg-warning-900/30 rounded-lg">
                <Target className="w-4 h-4 text-warning-600 dark:text-warning-400" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {formatCurrency(activeEvent.totalInvested || 0)}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Total Invertido</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventStatusCard;